import React, { useState, useEffect } from 'react';
import { Info, X } from 'lucide-react';

/**
 * DisclaimerBanner — Trust Infrastructure component.
 *
 * Persistent banner shown at the top of every Predict module. Dismissible per
 * session per module via sessionStorage. Spec from /app/memory/PREDICTIVE_FEASIBILITY.md:
 *   "Decision-support only. Verify with your local KVK / extension officer
 *    before acting."
 *
 * Props:
 *   module: string key — used as sessionStorage namespace (required)
 *   message: optional override of the default text
 *   tone: 'info' (default) | 'warning'
 */

const DEFAULT_MSG = (
  'This is decision-support, not advice. Always cross-verify with your local KVK / extension '
  + 'officer or trusted SAU agronomist before acting on these outputs.'
);

const TONE_CONFIG = {
  info: {
    bg: '#F5F2EC',
    fg: '#3F4A3D',
    accent: '#2D6A4F',
    border: '#E5E7DD',
  },
  warning: {
    bg: '#FFFBF1',
    fg: '#6B5500',
    accent: '#92400E',
    border: '#F5E5B8',
  },
};

export default function DisclaimerBanner({ module, message = DEFAULT_MSG, tone = 'info' }) {
  const storageKey = `predict_disclaimer_dismissed_${module}`;
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    try {
      const dismissed = sessionStorage.getItem(storageKey);
      if (dismissed === 'true') setVisible(false);
    } catch {
      // sessionStorage may be blocked; default to visible
    }
  }, [storageKey]);

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(storageKey, 'true');
    } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;
  const cfg = TONE_CONFIG[tone] || TONE_CONFIG.info;

  return (
    <div
      className="rounded-xl flex items-start gap-3"
      style={{
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        padding: '10px 14px',
        marginBottom: 18,
      }}
      role="note"
      data-testid={`disclaimer-banner-${module}`}
    >
      <Info size={16} style={{ color: cfg.accent, marginTop: 2, flexShrink: 0 }} />
      <div className="flex-1">
        <span
          className="font-body"
          style={{ fontSize: 12.5, color: cfg.fg, lineHeight: 1.55, fontWeight: 500 }}
        >
          <strong style={{ color: cfg.accent, fontWeight: 700, marginRight: 6 }}>Decision-support only.</strong>
          {message}
        </span>
      </div>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss disclaimer"
        style={{
          background: 'transparent',
          border: 'none',
          color: cfg.fg,
          cursor: 'pointer',
          padding: 4,
          marginTop: -2,
          marginRight: -4,
          opacity: 0.6,
          transition: 'opacity 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.6; }}
        data-testid={`disclaimer-dismiss-${module}`}
      >
        <X size={15} />
      </button>
    </div>
  );
}
